import { type MutableRefObject, useMemo, useRef } from 'react'

import {
  CustomFieldRenderProps,
  F0FormSubmitResult,
  F0WizardFormPerSectionSubmitArg,
  F0WizardFormStep,
  f0FormField,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { useSingleLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'
import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'
import { periodicityOptions } from 'modules/finance/lib/budgets'

import type { BudgetExceededAmounts, BudgetExceededOptions } from './types'

// -- Schema builders --

function buildBasicDetailsSchema(
  legalEntityId: ReturnType<typeof useSingleLegalEntitySelectorFormField>
) {
  return z.object({
    legalEntityId,
    name: f0FormField(z.string().min(1), {
      label: i18n.t('budgets.forms.new_budget.basic_information.budget_title'),
      placeholder: i18n.t('budgets.forms.new_budget.basic_information.budget_title_placeholder'),
      fieldType: 'text',
    }),
    description: f0FormField(z.string().optional(), {
      label: i18n.t('budgets.forms.new_budget.basic_information.budget_description'),
      placeholder: i18n.t(
        'budgets.forms.new_budget.basic_information.budget_description_placeholder'
      ),
      fieldType: 'textarea',
    }),
  })
}

function buildBudgetAccessSchema() {
  return z.object({
    ownerEmployeeIds: multiEmployeeSelectorV2FormField({
      label: i18n.t('budgets.forms.new_budget.budget_access.budget_owners'),
      mandatory: true,
      section: '',
      helpText: i18n.t('budgets.forms.new_budget.budget_access.budget_owners_description'),
      fieldConfig: {
        options: { disableSelectAll: true },
      },
    }),
    viewerEmployeeIds: multiEmployeeSelectorV2FormField({
      label: i18n.t('budgets.forms.new_budget.budget_access.budget_viewers'),
      mandatory: false,
      section: '',
      helpText: i18n.t('budgets.forms.new_budget.budget_access.budget_viewers_description'),
      fieldConfig: {
        options: { disableSelectAll: true },
      },
    }),
  })
}

function buildBudgetLimitsSchema(currencyRef: React.MutableRefObject<string>) {
  return z
    .object({
      effectiveFrom: f0FormField(z.date(), {
        label: i18n.t('budgets.forms.new_budget.budget_limits.start_date'),
        placeholder: i18n.t('budgets.forms.new_budget.budget_limits.start_date_placeholder'),
        fieldType: 'date',
        row: 'dates',
      }),
      effectiveUntil: f0FormField(z.date().optional(), {
        label: i18n.t('budgets.forms.new_budget.budget_limits.end_date'),
        placeholder: i18n.t('budgets.forms.new_budget.budget_limits.end_date_placeholder'),
        fieldType: 'date',
        row: 'dates',
      }),
      periodicity: f0FormField(z.enum(['daily', 'monthly', 'none', 'weekly', 'yearly']), {
        label: i18n.t('budgets.forms.new_budget.budget_limits.time_frame'),
        placeholder: i18n.t('budgets.forms.new_budget.budget_limits.select_time_frame'),
        fieldType: 'select',
        options: periodicityOptions(),
        row: 'periodicityAmount',
      }),
      amountCents: f0FormField(
        z.number().min(0.01, i18n.t('budgets.forms.errors.invalid_amount')),
        {
          label: i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame'),
          fieldType: 'custom',
          row: 'periodicityAmount',
          render: ({ value, onChange, disabled, error }: CustomFieldRenderProps<number>) => (
            <NumberInput
              required
              label={i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame')}
              locale={i18n.locale}
              units={currencyRef.current}
              value={value ?? null}
              onChange={(v: number | null) => onChange(v ?? 0)}
              disabled={disabled}
              error={error}
              maxDecimals={2}
              size="md"
            />
          ),
        }
      ),
    })
    .superRefine((data, ctx) => {
      if (data.effectiveUntil && data.effectiveFrom && data.effectiveFrom > data.effectiveUntil) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('budgets.forms.errors.start_date'),
          path: ['effectiveFrom'],
        })
      }
    })
}

function buildSpendersSchema(
  currencyRef: React.MutableRefObject<string>,
  budgetExceededRef: MutableRefObject<BudgetExceededAmounts | undefined>,
  budgetAmountOverrideRef: MutableRefObject<number | undefined>
) {
  return z.object({
    spendersField: f0FormField(
      z.object({
        allocationType: z.string(),
        allocatedAmountCents: z.number().optional(),
        spenderSelection: z.discriminatedUnion('type', [
          z.object({
            type: z.literal('manual'),
            ids: z.array(z.string()),
          }),
          z.object({
            type: z.literal('all'),
            filters: z.record(z.string(), z.unknown()).optional(),
            excludedIds: z.array(z.string()),
            includeInactive: z.boolean().optional(),
          }),
        ]),
      }),
      {
        label: '',
        fieldType: 'custom',
        render: ({
          value,
          onChange,
        }: CustomFieldRenderProps<{
          allocationType: string
          allocatedAmountCents?: number
          spenderSelection:
            | { type: 'manual'; ids: string[] }
            | {
                type: 'all'
                filters?: Record<string, unknown>
                excludedIds: string[]
                includeInactive?: boolean
              }
        }>) => (
          <SpendersFieldRenderer
            value={value}
            onChange={onChange}
            currency={currencyRef.current}
            budgetExceededAmounts={budgetExceededRef.current}
            onBudgetExceededOption={(option: BudgetExceededOptions) => {
              if (!budgetExceededRef.current) return
              const amounts = budgetExceededRef.current
              budgetExceededRef.current = undefined
              switch (option) {
                case 'increaseBudget':
                  budgetAmountOverrideRef.current = amounts.totalAllocatedAmount
                  onChange({ ...value, allocatedAmountCents: amounts.allocatedAmount })
                  break
                case 'changeAllocation':
                  budgetAmountOverrideRef.current = undefined
                  onChange({ ...value, allocatedAmountCents: amounts.maxAllocationAmount })
                  break
              }
            }}
          />
        ),
      }
    ),
  })
}

// -- Spenders field renderer (lazy import to avoid circular deps) --

import SpendersFieldRenderer from './SpendersField'

// -- Exported types --

function buildExpensesBudgetSchema(params: {
  legalEntityId: ReturnType<typeof useSingleLegalEntitySelectorFormField>
  currencyRef: React.MutableRefObject<string>
  budgetExceededRef: MutableRefObject<BudgetExceededAmounts | undefined>
  budgetAmountOverrideRef: MutableRefObject<number | undefined>
}) {
  return {
    basicDetails: buildBasicDetailsSchema(params.legalEntityId),
    budgetAccess: buildBudgetAccessSchema(),
    budgetLimits: buildBudgetLimitsSchema(params.currencyRef),
    spenders: buildSpendersSchema(
      params.currencyRef,
      params.budgetExceededRef,
      params.budgetAmountOverrideRef
    ),
  }
}

export type ExpensesBudgetSchema = ReturnType<typeof buildExpensesBudgetSchema>

export type ExpensesBudgetFormValues = {
  [K in keyof ExpensesBudgetSchema]: z.infer<ExpensesBudgetSchema[K]>
}

// -- Hook --

export interface UseExpensesBudgetFormParams {
  defaultOwnerEmployeeId: string
  defaultLegalEntityId: string | undefined
  currency: string
  currencyMap: Record<string, string>
  onSubmit: (
    arg: F0WizardFormPerSectionSubmitArg<ExpensesBudgetSchema>
  ) => Promise<F0FormSubmitResult>
  budgetExceededRef: MutableRefObject<BudgetExceededAmounts | undefined>
  budgetAmountOverrideRef: MutableRefObject<number | undefined>
}

export const useExpensesBudgetForm = ({
  defaultOwnerEmployeeId,
  defaultLegalEntityId,
  currency,
  currencyMap,
  onSubmit,
  budgetExceededRef,
  budgetAmountOverrideRef,
}: UseExpensesBudgetFormParams) => {
  const legalEntityId = useSingleLegalEntitySelectorFormField({
    mandatory: true,
    label: i18n.t('budgets.forms.new_budget.basic_information.legal_entity'),
    section: '',
  })

  const currencyRef = useRef<string>(currency)

  const wrappedOnSubmit = useMemo(
    () => async (arg: F0WizardFormPerSectionSubmitArg<ExpensesBudgetSchema>) => {
      if (arg.sectionId === 'basicDetails') {
        const selectedId = arg.data.legalEntityId
        if (selectedId && currencyMap[selectedId]) {
          currencyRef.current = currencyMap[selectedId]
        }
      }
      return onSubmit(arg)
    },
    [onSubmit, currencyMap]
  )

  const schema = useMemo(
    () =>
      buildExpensesBudgetSchema({
        legalEntityId,
        currencyRef,
        budgetExceededRef,
        budgetAmountOverrideRef,
      }),
    [legalEntityId, currencyRef, budgetExceededRef, budgetAmountOverrideRef]
  )

  const defaultValues = useMemo(
    () => ({
      basicDetails: {
        legalEntityId: defaultLegalEntityId,
        name: '',
        description: undefined,
      },
      budgetAccess: {
        ownerEmployeeIds: {
          type: 'manual',
          ids: [defaultOwnerEmployeeId],
        } satisfies { type: 'manual'; ids: string[] },
        viewerEmployeeIds: {
          type: 'manual',
          ids: new Array<string>(),
        } satisfies { type: 'manual'; ids: string[] },
      },
      budgetLimits: {
        effectiveFrom: undefined,
        effectiveUntil: undefined,
        periodicity: undefined,
        amountCents: undefined,
      },
      spenders: {
        spendersField: {
          allocationType: 'none',
          allocatedAmountCents: undefined,
          spenderSelection: {
            type: 'manual',
            ids: new Array<string>(),
          } satisfies { type: 'manual'; ids: string[] },
        },
      },
    }),
    [defaultOwnerEmployeeId, defaultLegalEntityId]
  )

  const sections = useMemo(
    () => ({
      basicDetails: {
        title: i18n.t('budgets.forms.new_budget.basic_information.title'),
        description: i18n.t('budgets.forms.new_budget.basic_information.description'),
      },
      budgetAccess: {
        title: i18n.t('budgets.forms.new_budget.budget_access.title'),
        description: i18n.t('budgets.forms.new_budget.budget_access.description'),
      },
      budgetLimits: {
        title: i18n.t('budgets.forms.new_budget.budget_limits.title'),
        description: i18n.t('budgets.forms.new_budget.budget_limits.description'),
      },
      spenders: {
        title: i18n.t('budgets.forms.new_budget.spenders.title'),
        description: i18n.t('budgets.forms.new_budget.spenders.description'),
      },
    }),
    []
  )

  const steps: F0WizardFormStep[] = useMemo(
    () => [
      {
        title: i18n.t('budgets.forms.new_budget.basic_information.title'),
        sectionIds: ['basicDetails'],
      },
      {
        title: i18n.t('budgets.forms.new_budget.budget_access.title'),
        sectionIds: ['budgetAccess'],
      },
      {
        title: i18n.t('budgets.forms.new_budget.budget_limits.title'),
        sectionIds: ['budgetLimits'],
      },
      {
        title: i18n.t('budgets.forms.new_budget.spenders.title'),
        sectionIds: ['spenders'],
        nextLabel: i18n.t('common.submit'),
      },
    ],
    []
  )

  const formDefinition = useF0FormDefinition({
    name: 'new-expenses-budget',
    schema,
    sections,
    defaultValues,
    onSubmit: wrappedOnSubmit,
  })

  return { formDefinition, steps }
}
